import * as React from 'react';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import type { RootState } from '@/reducers/reducers';
import { selectOrg, selectWorkflowId } from '@/selectors/selectors';
import { useGetRetainedBuildOptionsQuery } from '@/middlewares/defaultApi';
import { displayTextForRetainedBuildsStrategy } from '@/helpers/displayHelpers';
import {
    AppPlatform,
    AppStoreAvailableBuild,
    AppStoreBuildType,
    AppStoreType,
    IntegrationType,
    RetainedBuildsSettings,
    RetainedBuildsStrategy,
} from '@/types';
import { AppStoreBuildSelect } from '../selectedAppStoreBuild/AppStoreBuildSelect';
import Link from 'antd/lib/typography/Link';

interface Props {
    appId: string;
    stepConfigId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    isEditable: boolean;
    // Supplied by the wrapping Form.Item
    value?: RetainedBuildsSettings;
    onChange?: (config: RetainedBuildsSettings) => void;
}

const strategiesWhenRetaining = [RetainedBuildsStrategy.FromPreviousRelease, RetainedBuildsStrategy.FromSelection];

export const RetainedBuildsSettingsField: React.FC<Props> = (props) => {
    const orgId = useSelector((state: RootState) => selectOrg(state)?.id);
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));
    const { data, isFetching } = useGetRetainedBuildOptionsQuery(
        { orgId: orgId ?? '', workflowId: workflowId ?? '', stepConfigId: props.stepConfigId },
        { skip: !orgId || !workflowId },
    );

    const strategy = props.value?.strategy ?? RetainedBuildsStrategy.FromPreviousRelease;
    const buildIds = props.value?.buildIds ?? [];
    // A Play version code is both the build's id and the number we show, so the saved selection
    // renders the same before and after the available builds load
    const availableBuilds = data?.data?.availableBuilds ?? buildIds.map(placeholderBuildForVersionCode);
    const { onChange } = props;

    const change = (config: RetainedBuildsSettings) => onChange?.(config);

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Checkbox
                checked={strategy !== RetainedBuildsStrategy.None}
                disabled={!props.isEditable}
                onChange={(e) =>
                    change({
                        strategy:
                            e.target.checked ? RetainedBuildsStrategy.FromPreviousRelease : RetainedBuildsStrategy.None,
                        buildIds: [],
                    })
                }
            >
                Retain builds from previous releases
            </Checkbox>
            <Text type="secondary">{buildRetainedBuildsExplanation({ showDocsLink: false })}</Text>
            {strategy !== RetainedBuildsStrategy.None && (
                <Select
                    value={strategy}
                    disabled={!props.isEditable}
                    onChange={(nextStrategy) =>
                        change({
                            strategy: nextStrategy,
                            // Seed with what this app retains today, so pinning a selection doesn't silently drop those builds
                            buildIds:
                                nextStrategy === RetainedBuildsStrategy.FromSelection && buildIds.length === 0 ?
                                    (data?.data?.selectedRetainedBuildIds ?? [])
                                :   buildIds,
                        })
                    }
                >
                    {strategiesWhenRetaining.map((option) => (
                        <Select.Option key={option} value={option}>
                            {displayTextForRetainedBuildsStrategy(option)}
                        </Select.Option>
                    ))}
                </Select>
            )}
            {strategy === RetainedBuildsStrategy.FromSelection && (
                <AppStoreBuildSelect
                    appId={props.appId}
                    platform={props.platform}
                    appStoreType={props.appStoreType}
                    availableBuilds={availableBuilds}
                    includeAssociatedCIBuilds={false}
                    multiple
                    showRecentBuilds={false}
                    showPastBuildsEmptyState={false}
                    showsClearButton
                    placeholder={isFetching ? 'Loading builds…' : 'Select the builds to always submit'}
                    loading={isFetching}
                    disabled={isFetching || !props.isEditable}
                    notFoundContent={isFetching ? <Spin size="small" /> : 'No builds available'}
                    value={buildIds}
                    onChange={(value) => change({ strategy, buildIds: value as string[] })}
                />
            )}
        </Space>
    );
};

const placeholderBuildForVersionCode = (versionCode: string): AppStoreAvailableBuild => ({
    appStoreBuild: {
        id: versionCode,
        buildNumber: versionCode,
        uploadedAt: '',
        integrationId: IntegrationType.Google,
        type: AppStoreBuildType.AndroidAAB,
        isProcessed: true,
    },
    isRecentBuild: false,
    isCurrentlySelected: false,
    isNewestAppStoreBuild: false,
});

export const buildRetainedBuildsExplanation = ({ showDocsLink }: { showDocsLink: boolean }) => (
    <>
        Retained builds are submitted alongside this release&apos;s own build, so older, incompatible devices can
        continue to install a compatible build.
        {showDocsLink && (
            <>
                {' '}
                <Link
                    href="https://docs.runway.team/using-runway/release-steps/app-submission#android-retained-builds-additional-builds"
                    target="_blank"
                    style={{
                        color: 'inherit',
                        textDecorationLine: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: 3,
                    }}
                >
                    Learn more
                </Link>
            </>
        )}
    </>
);
